import { useMemo } from 'react';
import { Report, TestRun } from '@elementary/common-ui/types';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTableAssets from '@elementary/common-ui/hooks/useTableAssets';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';

const useReportTestRuns = ({ reportData }: { reportData?: Report }) => {
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const { testRunsData, modelsData } = useMemo(
    () => ({
      testRunsData: reportData?.test_runs,
      modelsData: reportData?.models,
    }),
    [reportData],
  );

  const { data: tableAssetsExtraDataMap, isFetching: isTableAssetsFetching } =
    useTableAssets();

  const enrichedTestRunsData = useMemo(() => {
    if (!testRunsData || isTableAssetsFetching) return testRunsData;

    const enrichedData: { [key: string]: TestRun[] } = {};

    Object.keys(testRunsData).forEach((modelKey) => {
      enrichedData[modelKey] = testRunsData[modelKey].map((testRun) => {
        if (tableAssetsExtraDataMap.size === 0) {
          // Report mode or no extra data available
          const { tags, owners } =
            modelsData?.[testRun.metadata.model_unique_id || ''] || {};

          if (tags || owners) {
            return {
              ...testRun,
              asset_metadata: {
                tags,
                owners,
              },
            };
          }
          return testRun;
        } else {
          const { isCritical, owners, tags, path, subscribers, name } =
            tableAssetsExtraDataMap.get(
              testRun.metadata.model_unique_id || '',
            ) || {};

          return {
            ...testRun,
            asset_metadata: {
              is_critical: isCritical,
              owners,
              tags,
              path,
              subscribers,
              name,
            },
          };
        }
      });
    });

    return enrichedData;
  }, [
    testRunsData,
    isTableAssetsFetching,
    tableAssetsExtraDataMap,
    modelsData,
  ]);

  const allTestRuns = useMemo(() => {
    const allTests: TestRun[] = [];
    if (!enrichedTestRunsData) return allTests;

    Object.keys(enrichedTestRunsData).forEach((modelKey) => {
      allTests.push(...(enrichedTestRunsData[modelKey] || []));
    });
    return distinctArrayBy(allTests, 'metadata.elementary_unique_id');
  }, [enrichedTestRunsData]);

  const tableData = useMemo(() => {
    if (!enrichedTestRunsData) return [];

    // No selection at all - show all test runs
    if (!selectedTreeNodeIds) {
      return allTestRuns;
    }

    // Something is selected - filter by selected node IDs
    const filteredTests: TestRun[] = [];

    selectedTreeNodeIds.forEach((nodeId) => {
      if (enrichedTestRunsData[nodeId]) {
        filteredTests.push(...enrichedTestRunsData[nodeId]);
      }
    });

    return distinctArrayBy(filteredTests, 'metadata.elementary_unique_id');
  }, [allTestRuns, enrichedTestRunsData, selectedTreeNodeIds]);

  return {
    tableData,
    filterOptions: null,
    isFetching: isTableAssetsFetching,
    isFilterOptionsFetching: false,
    fetchNextPage: undefined,
    hasNextPage: false,
    isError: false,
  };
};

export default useReportTestRuns;
